/**
 * Apple Merchant Setup — Plain Async Function
 *
 * Handles Apple Merchant (AM) setup by wrapping the ApplePaySession lifecycle
 * in a Promise. The session is created synchronously inside the Promise
 * constructor so it stays within the browser's trusted-event window when
 * called from a click handler.
 *
 * Supports two flows:
 * - Existing source: paymentSourceId > 0, uses it directly for merchant validation
 * - New source (from Add buttons): paymentSourceId = 0, creates source during merchant validation
 *
 * Referenced by the gateway registry's `performSetup` field for the `am` kind.
 */

import { validateApplePayMerchant, updateApplePayToken, addPaymentSource } from '../api';
import type { PerformSetupOptions } from '../config/gatewayRegistry';
import { SetupCancelledError } from './errors';

// ============================================================================
// Error Types
// ============================================================================

class SetupError extends Error {
  errorMessage?: string;

  errorTitle?: string;

  constructor(message: string, errorMessage?: string, errorTitle?: string) {
    super(message);
    this.name = 'SetupError';
    this.errorMessage = errorMessage;
    this.errorTitle = errorTitle;
  }
}

// ============================================================================
// Setup Function
// ============================================================================

export const performAppleMerchantSetup = (opts: PerformSetupOptions): Promise<void> => {
  const {
    context, paymentSourceId, analytics, log, onRefreshData
  } = opts;

  if (!context) {
    return Promise.reject(new SetupError('No context provided'));
  }

  if (typeof window === 'undefined' || !window.ApplePaySession) {
    return Promise.reject(new SetupError('ApplePaySession not available'));
  }

  return new Promise<void>((resolve, reject) => {
    let activeSourceId = paymentSourceId;

    analytics.trackStartAddingCard('am');
    log('info', 'Apple Merchant Setup', 'Starting setup', { paymentSourceId });

    let session: any;
    try {
      const sessionConfig = {
        ...context,
        automaticReloadPaymentRequest: {
          ...context.automaticReloadPaymentRequest,
          managementURL: context.automaticReloadPaymentRequest?.managementURL
            || context.automaticReloadPaymentRequest?.managementUrl
        }
      };
      session = new window.ApplePaySession!(6, sessionConfig);
    } catch (error) {
      log('error', 'Apple Merchant Setup', 'Session creation error', { error });
      analytics.trackAddCardFailed('session creation error');
      reject(new SetupError('Session creation failed'));
      return;
    }

    session.oncancel = () => {
      log('info', 'Apple Merchant Setup', 'User cancelled');
      analytics.trackAddCardFailed('user cancel');
      reject(new SetupCancelledError());
    };

    session.onvalidatemerchant = async ({ validationURL }: { validationURL: string }) => {
      try {
        if (!activeSourceId) {
          log('debug', 'Apple Merchant Setup', 'Adding payment source');
          const response = await addPaymentSource('am');
          activeSourceId = response.source.id;
        }

        log('debug', 'Apple Merchant Setup', 'Validating merchant');
        const merchantSession = await validateApplePayMerchant(activeSourceId, validationURL);
        session.completeMerchantValidation(merchantSession);
      } catch (error) {
        log('error', 'Apple Merchant Setup', 'Merchant validation failed', { error });
        session.abort();
        reject(new SetupError('Merchant validation failed'));
      }
    };

    session.onpaymentauthorized = async (event: any) => {
      const { payment } = event || {};

      if (!payment?.token) {
        session.completePayment(window.ApplePaySession!.STATUS_FAILURE);
        reject(new SetupError('No payment token'));
        return;
      }

      session.completePayment(window.ApplePaySession!.STATUS_SUCCESS);

      try {
        const { token, billingContact, shippingContact } = payment;
        await updateApplePayToken(activeSourceId, token, billingContact, shippingContact);

        log('info', 'Apple Merchant Setup', 'Setup completed successfully');
        analytics.trackCardAdded(activeSourceId, 'am');

        await onRefreshData?.();
        resolve();
      } catch (error: any) {
        log('error', 'Apple Merchant Setup', 'Failed to save token', { error });
        analytics.trackAddCardFailed('api error');
        const errorData = error?.errorData;
        const errorMsg = errorData?.error || errorData?.error_message;
        const errorTitle = errorData?.error_name;
        reject(new SetupError('Failed to save token', errorMsg, errorTitle));
      }
    };

    session.begin();
  });
};
